import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, ExternalLink, FileText, MessageCircle, Phone } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import Modal from '../../components/ui/Modal.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import {
  ensureHttpUrl,
  formatEmprendimientoPhone,
  getEmprendimientoWhatsAppHref,
} from '../../utils/emprendimientoContact.js';
import { stripHtmlToText } from '../../utils/seoMeta.js';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';

const DESC_FALLBACK = 'Consulta con el asociado para conocer más detalles de este emprendimiento.';
const DESC_PREVIEW_MAX = 140;

const descProse =
  'max-w-none break-words text-sm leading-relaxed text-slate-200 [&_a]:text-accent-lime [&_a]:underline hover:[&_a]:text-white [&_h1]:mb-2 [&_h1]:font-display [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-white [&_h2]:mb-2 [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-3 [&_p]:last:mb-0 [&_strong]:text-white [&_ul]:mb-3 [&_ol]:mb-3';

function getDescripcionPreview(descripcion) {
  const raw = (descripcion || '').trim();
  if (!raw) {
    return { preview: DESC_FALLBACK, needsMore: false, html: '' };
  }
  const plain = stripHtmlToText(raw, 10000);
  const hasHtml = /<[a-z][\s\S]*>/i.test(raw);
  const needsMore = hasHtml || plain.length > DESC_PREVIEW_MAX;
  const preview =
    plain.length > DESC_PREVIEW_MAX
      ? `${plain.slice(0, DESC_PREVIEW_MAX).trimEnd()}…`
      : plain || DESC_FALLBACK;
  return { preview, needsMore, html: raw };
}

function EmprendimientoCard({ item, delayMs, onVerMas }) {
  const waHref = getEmprendimientoWhatsAppHref(item);
  const pageHref = ensureHttpUrl(item.link_pagina);
  const phone = formatEmprendimientoPhone(item.telefono);
  const pdfHref = item.portafolio_pdf ? assetUrl(item.portafolio_pdf) : '';
  const { preview, needsMore } = getDescripcionPreview(item.descripcion);

  return (
    <ScrollReveal delayMs={delayMs} className="min-w-0">
      <PremiumCard hover className="flex h-full flex-col overflow-hidden !p-0">
        <div className="relative flex h-64 w-full shrink-0 items-center justify-center overflow-hidden bg-white p-3 ring-1 ring-slate-200 sm:h-72 lg:h-80">
          {item.imagen ? (
            <img
              src={assetUrl(item.imagen)}
              alt=""
              className="h-full w-full object-contain object-center"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <Briefcase className="h-12 w-12 text-accent-lime" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wider">Emprendimiento</span>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-6">
          <h3 className="break-words font-display text-xl font-semibold text-white">{item.nombre}</h3>
          <p className="mt-3 flex-1 break-words text-justify text-sm leading-relaxed text-slate-400">
            {preview}
          </p>
          {needsMore ? (
            <button
              type="button"
              onClick={() => onVerMas(item)}
              className="mt-2 self-start text-sm font-semibold text-accent-lime transition hover:text-white"
            >
              Ver más
            </button>
          ) : null}

          <div className="mt-5 flex flex-col gap-2">
            {phone ? (
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2 text-sm text-slate-300 transition hover:text-white"
              >
                <Phone className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                {phone}
              </a>
            ) : null}
            {pdfHref ? (
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-accent-lime transition hover:text-white"
              >
                <FileText className="h-4 w-4 shrink-0" aria-hidden />
                Ver portafolio (PDF)
              </a>
            ) : null}
            {pageHref ? (
              <a
                href={pageHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-accent-lime transition hover:text-white"
              >
                <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                Visitar página
              </a>
            ) : null}
          </div>

          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              Contactar por WhatsApp
            </a>
          ) : null}
        </div>
      </PremiumCard>
    </ScrollReveal>
  );
}

export default function EmprendimientosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const showLoading = useVisibleLoading(loading);

  const selectedWaHref = selected ? getEmprendimientoWhatsAppHref(selected) : null;
  const selectedPageHref = selected ? ensureHttpUrl(selected.link_pagina) : '';
  const selectedPdfHref = selected?.portafolio_pdf ? assetUrl(selected.portafolio_pdf) : '';
  const selectedPhone = selected ? formatEmprendimientoPhone(selected.telefono) : '';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/emprendimientos');
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar los emprendimientos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        eyebrow="Servicios"
        title="Emprendimientos de asociados"
        description="Conoce y apoya los negocios de nuestros asociados: productos, servicios y portafolios disponibles para la comunidad Coonaltragas."
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {error && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          <div className={error ? 'mt-10' : ''}>
            {showLoading && (
              <BrandLoading
                variant="inline"
                tone="light"
                label="Cargando emprendimientos…"
                className="min-h-[40vh]"
              />
            )}

            {!showLoading && !error && items.length === 0 && (
              <p className="text-center text-slate-600">
                No hay emprendimientos publicados por ahora.
              </p>
            )}

            {!showLoading && items.length > 0 && (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {items.map((item, i) => (
                  <EmprendimientoCard
                    key={item.id}
                    item={item}
                    delayMs={Math.min(i, 8) * 45}
                    onVerMas={setSelected}
                  />
                ))}
              </div>
            )}
          </div>

          {!showLoading && (
            <p className="mt-14 text-center text-sm text-slate-600">
              ¿Eres asociado y quieres publicar tu emprendimiento?{' '}
              <Link to="/contacto" className="font-semibold text-accent-lime hover:text-[#0d2b7f]">
                Contáctanos
              </Link>
              .
            </p>
          )}
        </div>
      </section>

      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.nombre || 'Emprendimiento'}
      >
        {selected ? (
          <div className="space-y-5">
            {selected.imagen ? (
              <div className="flex h-56 w-full items-center justify-center overflow-hidden rounded-xl bg-white p-3 sm:h-64">
                <img
                  src={assetUrl(selected.imagen)}
                  alt=""
                  className="h-full w-full object-contain object-center"
                />
              </div>
            ) : null}
            {(selected.descripcion || '').trim() ? (
              <div
                className={descProse}
                dangerouslySetInnerHTML={{ __html: selected.descripcion }}
              />
            ) : (
              <p className="text-base leading-relaxed text-slate-200">{DESC_FALLBACK}</p>
            )}
            <div className="space-y-2 text-sm">
              {selectedPhone ? (
                <a
                  href={`tel:${selectedPhone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-white"
                >
                  <Phone className="h-4 w-4 text-accent-lime" aria-hidden />
                  {selectedPhone}
                </a>
              ) : null}
              {selectedPdfHref ? (
                <a
                  href={selectedPdfHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-accent-lime hover:text-white"
                >
                  <FileText className="h-4 w-4" aria-hidden />
                  Descargar portafolio (PDF)
                </a>
              ) : null}
              {selectedPageHref ? (
                <a
                  href={selectedPageHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-accent-lime hover:text-white"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  Visitar página web
                </a>
              ) : null}
            </div>
            {selectedWaHref ? (
              <a
                href={selectedWaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />
                Contactar por WhatsApp
              </a>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
